// src/services/sessionService.ts
/**
 * Session Management Service
 * Handles session state across all browser tabs/windows
 * No authentication required - purely session-based tracking
 */
import type { SessionData } from '../types/session';

const SESSION_STORAGE_KEY = 'travelers_session';

export type { SessionData };

/**
 * Extract session data from URL parameters
 * Returns null as URL parameters are no longer used
 */
export const extractSessionFromUrl = (): Partial<SessionData> | null => {
  return null;
};

/**
 * Store session data in localStorage (shared across all browser tabs)
 */
export const storeSession = (sessionData: SessionData): void => {
  try {
    localStorage.setItem(SESSION_STORAGE_KEY, JSON.stringify(sessionData));
  } catch (error) {
    console.error('Failed to store session:', error);
  }
};

/**
 * Retrieve session data from localStorage
 */
export const getSession = (): SessionData | null => {
  try {
    const sessionStr = localStorage.getItem(SESSION_STORAGE_KEY);
    if (sessionStr) {
      return JSON.parse(sessionStr) as SessionData;
    }
  } catch (error) {
    console.error('Failed to retrieve session:', error);
  }
  return null;
};

/**
 * Check if user has an active session
 */
export const hasActiveSession = (): boolean => {
  const session = getSession();
  return session !== null && !session.isLoggedOut;
};

/**
 * Create a new session automatically on app launch
 * This is called when no session exists
 */
export const createAutoSession = (): SessionData => {
  const sessionData: SessionData = {
    timestamp: Date.now(),
    isLoggedOut: false,
  };
  storeSession(sessionData);
  return sessionData;
};

/**
 * Mark session as logged out
 */
export const markSessionAsLoggedOut = (): void => {
  // Clear any stored session so a full browser restart can create a fresh session
  try {
    localStorage.removeItem(SESSION_STORAGE_KEY);
  } catch (error) {
    console.error('Failed to clear session:', error);
  }
};

/**
 * Initialize session from URL parameters if available
 * Returns false as URL parameters are no longer used
 */
export const initializeSessionFromUrl = (): boolean => {
  return false;
};




